import { button, eventTag, line } from "./shared";
import type { EventId } from "../../world/types";

/*
 * The lives the world offers, and the words it says once one has been taken
 * (chunk U-0's split of `panels.ts`; chunk U-4's carousel and waking).
 *
 * **It is a beat of one sequence rather than a panel of its own** (plan §5
 * "U-4", `ui-lab/surfaces/opening.html`). The book of eras, the lives, the name
 * and the waking used to be four boxes in a row, each dismissed before the next
 * arrived; they are four beats now, read against one veil that never lifts
 * between them. What died with that is the **bridge** — a whole screen holding
 * the single line *"and where do you enter it?"* — because it was a hinge
 * between two surfaces that no longer need one. The question survives verbatim
 * as this beat's lead, said quietly over the answers.
 *
 * So the box is gone: no plate, no border, no corner in the frame. What is on
 * the screen is a lead, a strip of cards, and one way out, centred on the dark
 * (`ui/opening.css`).
 *
 * The death beat re-uses this screen with the epitaph as its lead
 * (`main.ts`'s `mourn()`), which is why "origins" is the honest name for the
 * file and "the birth screen" is not — and why the lead is drawn in the same
 * register whichever sentence it is carrying.
 */

/**
 * How long the veil takes to lift once the sequence is over, in seconds.
 *
 * The lab's own number (`opening.html`'s `.world-scrim`, 1.4s): slow enough
 * that no single frame of it is an event, which is the whole point — a scrim
 * that snaps is a light switch, and what a player should notice is that the
 * street was dark when they started reading and is a morning by the time they
 * are standing in it.
 */
const VEIL_LIFT_SECONDS = 1.4;

/**
 * How long the world's first words stand, and how long they take to go.
 *
 * **The spoken line's own clock, restated rather than shared**: seven seconds
 * is a sentence read at walking pace and 1.6 is what every other line in this
 * client fades over (`hud/panels/dialogue.ts`). It is the same band in the same
 * place, so it holds for the same time; the two numbers are typed twice because
 * that file does not export them and it is fenced out of this chunk. Filed.
 */
const WAKING_HOLD_SECONDS = 7;
const WAKING_FADE_SECONDS = 1.6;

/** The portrait frame's honest empty state (plan D12 — portraits await a body renderer). */
const NO_LIKENESS = "no likeness recorded";

/** One life the opening offers, as it is drawn. See {@link OriginsPanel}. */
export interface OriginCardView {
  /** The card's line: *"an orphan of ruined Eldbeck"* (`witness/origins.ts`). */
  readonly line: string;

  /**
   * What the record says about the birth, or `""` where this client cannot
   * honestly say anything.
   *
   * **Only the absence is drawable today.** Two of the four doors are *defined*
   * by having no birth on record — an orphan's is in no chronicle and an
   * unrecorded child's was never written down, which is what the simulation's
   * own waking sentence says of both (`docs/decisions/speech.md`, chunk S-1) —
   * so the caller may state that. A *bound* life has a birth record and this
   * client cannot read its year: `OriginCard` carries no birth date
   * (`world/types.ts`), so the row is left out rather than filled with a
   * guess. See {@link OriginsPanel} for the seam that was filed instead.
   */
  readonly born: string;

  /**
   * The seats this life's parents held — *"child of the ruler of Eldbeck"* —
   * one per line under the card, or none at all.
   *
   * They are the only thing that makes one card *different in kind* from
   * another, which is why they are drawn rather than counted: a player choosing
   * between three lives is choosing between what the world will already owe
   * them, and *"child of the ruler of somewhere"* is the whole of that
   * information (`fantasy-01-born-into-the-world.md` §2).
   */
  readonly claims: readonly string[];

  /**
   * The record the offer rests on: the sack an orphan survived, the birth a
   * child was written down by.
   *
   * **A reversal of this file's Phase 6 rule, argued rather than dropped.** The
   * screen used to carry no record tags at all, on the Book of Eras' reasoning —
   * a diegetic surface carrying handle numbers is the mistake wave 4 of the
   * voice overhaul made once. A card is not that surface: `witness/origins.ts`
   * says so in its own header — *a card is a label on a menu of lives rather
   * than a sentence about a record* — and a menu is the client talking. What
   * the lab drew is a **dossier**, whose whole design is *only what the record
   * supports*, and a dossier that will not name the record it rests on is
   * asking to be taken on trust. Drawn dim, at the card's foot, in the kit's
   * `.cite` (plan §5 "U-4"; `ui-lab/surfaces/opening.html` beat 2). The trace
   * view still carries every one of them, which is where a reader goes to find
   * out what a record *says*.
   */
  readonly cites: readonly EventId[];
}

/** Everything the lives beat draws. See {@link OriginsPanel.show}. */
export interface OriginsView {
  /**
   * The sentence the lives are offered under.
   *
   * *"and where do you enter it?"* at the start of a session, and the log's own
   * words about the killing at the end of a life (`main.ts`'s `mourn()`). One
   * field for both because it is one moment asked twice, and the register is
   * the same either way: prose the world is saying, over the answers it is
   * about to give.
   */
  readonly lead: string;

  /** The lives, in the order the source listed them. */
  readonly cards: readonly OriginCardView[];

  /** Which card is centred. Clamped here, so a caller may count past the end. */
  readonly cursor: number;

  /** The foot line: how to choose, and how to leave. */
  readonly hint: string;

  /** Takes a life. The index is into {@link OriginsView.cards}. */
  readonly onChoose: (card: number) => void;

  /** Brings a life to the centre without taking it. */
  readonly onCursor: (card: number) => void;

  /** Leaves without being born — a real answer, and the Phase 5 session. */
  readonly onDecline: () => void;
}

/**
 * The lives this world can honestly offer, as a strip of dossiers.
 *
 * Phase 6 plan, wave 3C, over decision 3 — the origins screen the fantasy asks
 * for: *"after the history intro, the player chooses what kind of character to be
 * born as"* — restaged by chunk U-4 as the second beat of one continuous opening
 * (the owner's call of 2026-09-01: *the lives as cards the player scrolls through
 * sideways, not a vertical register*). A card has room to grow as the simulation
 * exposes more of a life; a row of a list does not.
 *
 * Four properties are load-bearing and each is a rule rather than a taste:
 *
 * - **Every step is skippable**, which is this client's standing answer to every
 *   first-run moment it puts up ({@link NamePrompt}, `hud/opening.ts`). Escape at
 *   the lives, escape at the name — and a session that declines is the Phase 5
 *   first minute exactly as it shipped.
 * - **Nothing on a card is composed here.** The line and the claims arrive as
 *   strings assembled from the simulation's own nouns (`witness/origins.ts`), and
 *   what this file decides is which one is centred.
 * - **Only what the record supports is on a card.** The birth row is the
 *   absence of a record and never a year this client worked out; the portrait
 *   frame says what it does not have; a life with no seats behind it shows none.
 * - **No gender**, and it is not an omission: `Figure` carries no such field, so
 *   a card that showed one would be the client inventing a fact about a person
 *   the log has been keeping for two hundred years (plan D12).
 *
 * **What this file cannot draw yet, filed rather than faked.** The lab's card
 * carries a *home* row and a *born year* row. `OriginChoice` keeps neither: the
 * town's name is spent inside the card's line and dropped, and no export
 * anywhere carries a figure's birth year (`WitnessSource.origins` — checked).
 * Both want one field on `witness/origins.ts`'s `OriginChoice`, which chunk S-1
 * has already filed as the file whose composing should move sim-side; this
 * chunk is fenced out of it and adds nothing to it.
 */
export class OriginsPanel {
  /** What the beat is read against, and — like the book's — the way out. */
  private readonly backdrop: HTMLElement;

  private readonly element: HTMLElement;
  private readonly leadElement: HTMLElement;

  /** The window the strip slides behind, and the strip itself. */
  private readonly carousel: HTMLElement;
  private readonly track: HTMLElement;

  private readonly buttonsElement: HTMLElement;
  private readonly hintElement: HTMLElement;

  /** The world's first words to the player, and the last beat of the sequence. */
  private readonly waking: WakingLine;

  /** Which card is centred, so a caller that only wants to move it can ask. */
  private at = 0;

  /** How many lives are on offer, so a move can be clamped without one. */
  private offered = 0;

  /**
   * The lives on the strip as they were last drawn — their line and their seats
   * — so a *move* can be told from a *new list*. Empty while the beat is shut.
   *
   * The one thing this panel remembers between calls, and it is remembered for
   * the reason {@link OriginsPanel.show} gives: the cards are what the pointer is
   * standing on, and rebuilding them under it is what took the mouse away from
   * this screen once already.
   */
  private drawn: readonly string[] = [];

  /** The veil's own timer while it is lifting, so a second beat can cancel it. */
  private lifting: number | null = null;

  constructor(root: HTMLElement) {
    this.backdrop = document.createElement("div");
    this.backdrop.className = "hud__eras-back";
    this.backdrop.hidden = true;

    // **Not a `hud__panel`**, on the reading window's own reasoning (chunk U-2,
    // `panels/shared.ts`): the chassis in `ui/panel.css` is a box in a corner
    // with a plate and a padding, and this is a full-frame layer with nothing
    // behind it but the veil. The surface's `hud__panel--origins` name is the
    // identity and stays (plan D2); `ui/opening.css` states the `[hidden]` rule
    // the chassis used to lend it, because an author `display` beats the user
    // agent's however specific either is.
    this.element = document.createElement("div");
    this.element.className = "hud__panel--origins hud__opening";
    this.element.hidden = true;

    this.leadElement = document.createElement("div");
    this.leadElement.className = "hud__origins-lead";

    this.track = document.createElement("div");
    this.track.className = "hud__origins-track";
    // The strip is a list somebody chooses from, and each card is one option —
    // which is what makes `aria-selected` below mean anything at all. The lab's
    // own markup (`ui-lab/surfaces/opening.html`), and the label is in the same
    // lower case every other string this client shows is in.
    this.track.setAttribute("role", "listbox");
    this.track.setAttribute("aria-label", "the lives on offer");

    this.carousel = document.createElement("div");
    this.carousel.className = "hud__origins-carousel";
    this.carousel.append(this.track);

    this.buttonsElement = document.createElement("div");
    this.buttonsElement.className = "hud__origins-out";

    this.hintElement = document.createElement("div");
    this.hintElement.className = "hud__panel-hint";

    const column = document.createElement("div");
    column.className = "hud__opening-column";
    column.append(this.leadElement, this.carousel, this.buttonsElement);

    // The hint is outside the column and inside the beat, which is the reading
    // window's rule one surface further on: the keys belong to the player and
    // the page to the world (chunk U-2, `panels/shared.ts`).
    this.element.append(column, this.hintElement);

    this.waking = new WakingLine();
    // The backdrop first, so the beat is drawn over it — the document-order rule
    // the panels are attached under (`hud/hud.ts`). The waking band goes on
    // after both, so the sentence stands over a veil that is on its way out.
    root.append(this.backdrop, this.element);
    this.waking.attach(root);

    // **Re-centred on resize**, because the strip is positioned in pixels off a
    // measured box and the box changes with the window. It is bound once for the
    // life of the HUD rather than per opening: this panel is built at boot and
    // never torn down, and a listener added and removed around a promise is a
    // listener that leaks the day somebody adds a second way out.
    window.addEventListener("resize", () => {
      if (this.open) {
        this.centre();
      }
    });
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  /** Which life is centred. `0` when the beat is shut. */
  get cursor(): number {
    return this.at;
  }

  /** How many lives are on offer. `0` when the beat is shut. */
  get count(): number {
    return this.offered;
  }

  /**
   * Draws the lives and shows the beat.
   *
   * Called again for every move, like every other panel here: a view goes in and
   * the page comes out of it, with no caller ever told which part of the screen
   * it changed.
   *
   * **The cards are the exception, and a bug is why** (owner report, 2026-08-24:
   * *"clicking the offered lives does nothing"*). A browser will not raise a
   * `click` on a node that was taken out of the document between the press and
   * the release: it retargets both at the surviving parent and no click is
   * generated at all. The gesture that moves the strip is now a click on a card
   * that is not centred ({@link OriginsPanel.dressCard}), so that trap is one
   * press closer than it was — the press that moves the strip is followed by the
   * press that takes the life, and the second one lands on a node the first one
   * must not have replaced.
   *
   * So a draw of the *same lives* moves a class, re-binds the gesture and slides
   * the strip; anything else — a different list, an empty one — is the whole
   * beat again.
   */
  show(view: OriginsView): void {
    this.offered = view.cards.length;
    this.at = Math.min(Math.max(view.cursor, 0), Math.max(view.cards.length - 1, 0));

    // Put back rather than assumed: {@link OriginsPanel.veil} may have left the
    // backdrop up as a still one and {@link OriginsPanel.lift} as a lifting one,
    // and a beat drawn after either is clickable again.
    this.stopLifting();
    this.backdrop.className = "hud__eras-back";
    this.backdrop.hidden = false;
    // And the last beat's sentence goes with the veil coming back up. A figure
    // killed in the first seconds of a life would otherwise be mourned under the
    // words the world woke them with (`main.ts`'s `mourn()` opens this beat over
    // a running session, which is the one place the two can overlap).
    this.waking.say("");

    this.leadElement.textContent = view.lead;

    // Everything a card draws, so "the same lives" is a claim about the strip
    // and not only about its first line.
    const lives = view.cards.map((card) =>
      [card.line, card.born, ...card.claims, ...card.cites.map(eventTag)].join("\n"),
    );
    const moving =
      lives.length > 0 &&
      lives.length === this.drawn.length &&
      lives.every((life, index) => this.drawn[index] === life);

    if (moving) {
      for (const [index, card] of [
        ...this.track.querySelectorAll<HTMLElement>(".hud__origins-card"),
      ].entries()) {
        this.dressCard(card, index, view);
      }
    } else {
      this.track.replaceChildren(
        ...(view.cards.length === 0
          ? [line("this world has no life to offer yet")]
          : view.cards.map((card, index) => this.renderCard(card, index, view))),
      );
      this.drawn = lives;
    }

    this.buttonsElement.replaceChildren(
      turner("none of these", () => {
        view.onDecline();
      }),
    );
    this.hintElement.textContent = view.hint;
    this.element.hidden = false;
    this.centre();

    // The backdrop's click declines, rebound with the view because the callback
    // belongs to whoever opened the beat. Declining rather than choosing: a
    // click into the dark is not a life somebody picked.
    this.backdrop.onclick = (event: MouseEvent): void => {
      event.stopPropagation();
      view.onDecline();
    };
  }

  /**
   * Puts the darkened frame up with nothing on it.
   *
   * **What every naming step is read against** (`hud/origins.ts`). Two of them
   * reach it: the menu naming step, in the sessions that were never offered a
   * life — `?born=0`, a world with nothing to offer, a build with no door, a
   * player who declined the ones there were — and the birth's own naming, which
   * carries the chosen card into its own beat and so leaves nothing here to
   * stand behind it. The alternative to this method is a question floating over
   * an undrawn world with the session's own chrome showing through it, which is
   * the exact frame a screenshot caught in wave 3C.
   *
   * The backdrop's click is unbound here rather than rebound, because there is
   * nothing left to decline *into*: the card over it has two ways out and both
   * of them are answers.
   */
  veil(): void {
    this.stopLifting();
    this.backdrop.className = "hud__eras-back hud__eras-back--still";
    this.backdrop.hidden = false;
    this.backdrop.onclick = null;
    this.element.hidden = true;
  }

  /**
   * The last beat: the veil lifts on the street, and the world says its first
   * words to whoever is standing in it.
   *
   * **It does not block, and that is the whole design of it.** The frame loop
   * has not started while the start menus are up — `main.ts` calls `loop.start()`
   * after they resolve, and nothing draws the world before that — so a beat that
   * waited here would lift a veil off a black frame. Started instead and left to
   * play out: the caller resolves, `main.ts` claims the name and starts the loop
   * in the same task, and the street is being drawn by the time the veil is
   * half gone. What a player sees is the dark going and the sentence arriving
   * over the morning, which is what the beat is for
   * (`ui-lab/surfaces/opening.html` beat 4).
   *
   * `said` is the simulation's sentence and is never composed here
   * (`WitnessSource.wakingLine`, chunk S-1). The empty string is a real answer —
   * a session that took no life, a fixture that offers none, a build whose
   * boundary does not carry the read — and it is answered with silence rather
   * than with a line about a life nobody chose.
   */
  lift(said: string): void {
    this.element.hidden = true;
    this.drawn = [];
    this.offered = 0;
    this.at = 0;
    this.stopLifting();

    if (!this.backdrop.hidden) {
      // A class rather than a fade written from script: the transition is
      // `ui/opening.css`'s and the timer below only takes the element away once
      // it is over. The crosshair stays suppressed for the length of the lift,
      // because the rule asks whether the backdrop is `hidden` and it is not yet
      // (`ui/reading.css`) — which is right, since there is nothing to aim at
      // until the player is holding the pointer.
      this.backdrop.className = "hud__eras-back hud__eras-back--lifting";
      this.backdrop.onclick = null;
      this.lifting = window.setTimeout(() => {
        this.lifting = null;
        this.backdrop.hidden = true;
        this.backdrop.className = "hud__eras-back";
      }, VEIL_LIFT_SECONDS * 1000);
    }

    this.waking.say(said);
  }

  hide(): void {
    this.stopLifting();
    this.element.hidden = true;
    this.backdrop.hidden = true;
    this.backdrop.className = "hud__eras-back";
    this.backdrop.onclick = null;
    this.offered = 0;
    this.at = 0;
    this.drawn = [];
  }

  /**
   * Slides the strip so the centred card is in the middle of its window.
   *
   * The lab's arithmetic exactly (`opening.html`'s `centreLife`): the card's
   * offset plus half its width, less half the window's. Written in pixels
   * because that is what `offsetLeft` answers in, and re-run on every draw and
   * every resize.
   *
   * A strip drawn into a document nobody attached measures zero — which is every
   * self-check in this tree (`dev/selfcheck/figure.ts` draws into a detached
   * root) — and zero is a legitimate answer here rather than a case to guard:
   * the offset comes out as `0` and the strip sits where it started.
   */
  private centre(): void {
    const card = this.track.children[this.at];

    if (!(card instanceof HTMLElement)) {
      this.track.style.transform = "";

      return;
    }

    const offset = card.offsetLeft + card.offsetWidth / 2 - this.carousel.clientWidth / 2;

    this.track.style.transform = `translateX(${-offset}px)`;
  }

  /** Cancels a lift in flight, so a second beat does not have the first's timer. */
  private stopLifting(): void {
    if (this.lifting !== null) {
      window.clearTimeout(this.lifting);
      this.lifting = null;
    }
  }

  /**
   * One life: a portrait frame with nothing in it, the card's line, what the
   * record does and does not say, and the record it rests on.
   *
   * What makes it a *card somebody chooses* rather than a paragraph is
   * {@link OriginsPanel.dressCard}, which is where the centre and the gesture
   * live — and it is separate from this because a life that is already on the
   * strip is dressed again without being built again.
   *
   * A `<button>` because it is one: the whole card is the target, on a phone as
   * much as under a pointer, and a `<div>` with a click on it is a target the
   * keyboard cannot reach.
   */
  private renderCard(card: OriginCardView, index: number, view: OriginsView): HTMLElement {
    const element = document.createElement("button");
    element.type = "button";
    element.setAttribute("role", "option");

    fillOriginCard(element, card);
    this.dressCard(element, index, view);

    return element;
  }

  /**
   * Whether this life is the centred one, and the gesture it answers.
   *
   * **A click on a card that is not centred brings it to the centre; a click on
   * the centred one takes it.** Two presses to take a life with a mouse, which
   * is the carousel's own gesture and is what replaced the hover-follows-pointer
   * rule the list had: on a strip, a cursor that followed the pointer would
   * lurch the whole row every time somebody crossed it on the way to the card
   * they wanted.
   *
   * Rebound on every draw rather than bound once, because the callbacks belong to
   * whoever opened the beat (the backdrop's click is rebound for the same
   * reason): a card that outlives a redraw must not go on answering to the view
   * that built it.
   */
  private dressCard(element: HTMLElement, index: number, view: OriginsView): void {
    const centred = index === this.at;

    element.className = centred
      ? "hud__origins-card hud__origins-card--at"
      : "hud__origins-card";
    element.setAttribute("aria-selected", centred ? "true" : "false");
    element.onclick = (): void => {
      if (centred) {
        view.onChoose(index);

        return;
      }

      view.onCursor(index);
    };
  }
}

/**
 * Fills an element with one life's dossier.
 *
 * Exported because **two beats draw the same card**: the lives, and the naming
 * that carries the chosen one over into its own column (`hud/panels/name.ts`,
 * the owner's call of 2026-09-01 — *chosen card on the left, the asking on the
 * right*). It fills rather than builds so each beat may choose its own element:
 * a `<button>` in the strip, where the card is the thing being pressed, and an
 * `<aside>` beside the field, where it is being asked about and is inert.
 *
 * It lives here rather than in `panels/shared.ts` — whose rule is that a helper
 * lands there the moment a second surface wants it — because moving it would
 * have moved {@link OriginCardView} with it, and that type is re-exported from
 * `hud/panels.ts` by this file's name.
 *
 * **A row with nothing behind it is left out rather than filled**, which is the
 * origins seam's own honesty rule at the sentence level (`witness/origins.ts`)
 * and the whole reason a dossier is worth drawing at all.
 */
export function fillOriginCard(host: HTMLElement, card: OriginCardView): void {
  const portrait = document.createElement("span");
  portrait.className = "hud__origins-portrait";
  // Decorative and said so: there is no likeness, and a screen reader has no use
  // for a frame that says it is empty.
  portrait.setAttribute("aria-hidden", "true");

  const diamond = document.createElement("span");
  diamond.className = "objective-diamond hollow";

  const missing = document.createElement("span");
  // Its own name rather than the frame's only child, because a driver asking
  // *what does the portrait say* must not get the diamond beside it
  // (`web/scripts/shots.mjs`).
  missing.className = "hud__origins-likeness";
  missing.textContent = NO_LIKENESS;
  portrait.append(diamond, missing);

  const said = document.createElement("p");
  said.className = "hud__origins-line";
  said.textContent = card.line;

  const meta = document.createElement("div");
  meta.className = "hud__origins-meta";

  if (card.born !== "") {
    const born = document.createElement("p");
    born.className = "hud__origins-born";
    born.textContent = card.born;
    meta.append(born);
  }

  for (const claim of card.claims) {
    const held = document.createElement("p");
    held.className = "hud__origins-claim";
    held.textContent = claim;
    meta.append(held);
  }

  host.replaceChildren(portrait, said);

  // A card with no birth row and no seats gets no rule across it either: the
  // hairline is what separates the line from what is under it, and there is
  // nothing under it.
  if (meta.childElementCount > 0) {
    host.append(meta);
  }

  if (card.cites.length > 0) {
    const cite = document.createElement("span");
    cite.className = "hud__origins-cite";
    cite.textContent = "cites ";

    for (const event of card.cites) {
      const tag = document.createElement("b");
      tag.className = "cite";
      tag.textContent = eventTag(event);
      cite.append(tag);
    }

    host.append(cite);
  }
}

/** One button under the strip: {@link button} wearing the kit's shape. */
function turner(label: string, onPress: () => void): HTMLButtonElement {
  const element = button(label, onPress);

  element.classList.add("panel-button");

  return element;
}

/**
 * The world's first words to the player, in the band its words always arrive in.
 *
 * The opening's payoff beat (`ui-lab/surfaces/opening.html` beat 4): the veil
 * goes, the street is there, and one sentence says who is standing in it. The
 * sentence is the simulation's — `speech::waking`, one clause per origin door,
 * with an honest form for a life nobody named (`docs/decisions/speech.md`, chunk
 * S-1) — and this draws it and composes nothing.
 *
 * **It wears the kit's subtitle track and is not a {@link DialogueLine}**, which
 * is two decisions in one. The register is right: the world's first words to you
 * should arrive the way its words always will, centred at the foot of the frame
 * with a gold hairline over and under. The *clock* is not: the overheard line's
 * band belongs to the HUD and is driven by earshot at nine tiles
 * (`witness/staging.ts`), and a sentence about a birth has nothing to do with
 * who is standing nearby. So it is this flow's own element, with the same two
 * numbers written down twice.
 */
class WakingLine {
  private readonly element: HTMLElement;
  private readonly said: HTMLElement;

  /** The hold, then the fade, so a second waking cancels the first's. */
  private timer: number | null = null;

  constructor() {
    this.element = document.createElement("div");
    this.element.className = "hud__waking";
    this.element.hidden = true;

    const track = document.createElement("div");
    track.className = "hud__waking-track subtitle-track";

    this.said = document.createElement("p");
    this.said.className = "hud__waking-line subtitle-line";

    track.append(this.said);
    this.element.append(track);
  }

  /**
   * Attaches the band to the HUD, after whatever built it.
   *
   * Separate from the constructor because **the order a thing is appended in is
   * the order it is drawn in** (`hud/hud.ts` — there is no `z-index` anywhere in
   * this tree), and the band has to be over the veil that is lifting under it.
   */
  attach(root: HTMLElement): void {
    root.append(this.element);
  }

  /**
   * Says it, holds it, and takes it away.
   *
   * The empty string says nothing at all and shows nothing: a fixture offers no
   * lives so it has no sentence, a session that took no life has no row to ask
   * about, and both are answered with the silence the boundary answered with
   * (`world/fixture.ts`, `world/types.ts`'s `wakingLine`).
   */
  say(text: string): void {
    this.stop();

    if (text === "") {
      this.element.hidden = true;

      return;
    }

    this.said.textContent = text;
    this.element.className = "hud__waking";
    this.element.hidden = false;

    this.timer = window.setTimeout(() => {
      // The going is a class and the transition is the stylesheet's; the second
      // timer only takes the element off the page once it is over.
      this.element.className = "hud__waking hud__waking--going";
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.element.hidden = true;
      }, WAKING_FADE_SECONDS * 1000);
    }, WAKING_HOLD_SECONDS * 1000);
  }

  private stop(): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
